/**
 * Hold arithmetic and vocabulary — shared by the composer (HoldComposer),
 * the hold cards (HoldCard) and the inquiry detail page that hosts both.
 *
 * ---------------------------------------------------------------------------
 * THE TIMEZONE RULE — read this before touching anything below
 * ---------------------------------------------------------------------------
 * Two different kinds of value arrive from the backend and they must not be
 * treated the same way:
 *
 *   hold_created_at   full timestamptz, e.g. "2026-08-28T13:26:24.347+00:00"
 *   hold_expires_at   date-only string,  e.g. "2026-10-18"
 *
 * Every calculation here reduces both to a UTC calendar day, and every date
 * is FORMATTED with { timeZone: 'UTC' }. That is not decoration — it is the
 * only thing that keeps this screen agreeing with the server:
 *
 *   • The server derives hold_expires_at as UTC(hold_created_at) + N days
 *     (inquiryController.computeHoldExpiresAt), and stores it in a `date`
 *     column. Parsing "2026-10-18" with `new Date(iso)` yields UTC midnight;
 *     rendering that through the default local formatter shows "Oct 17" to
 *     everyone west of Greenwich. That is the classic off-by-one and it is
 *     exactly the one a mediator would act on.
 *
 *   • "Today" is likewise taken in UTC. It has to be: the follow-up date the
 *     composer previews is computed from `new Date()` the same way the server
 *     will compute it from its own `heldAt` stamp a few hundred milliseconds
 *     later. Anchoring the preview to a local calendar day would make the
 *     sentence on screen disagree with the date that actually gets written
 *     whenever the two calendars have already diverged (any US evening).
 *
 * The consequence, stated plainly so nobody "fixes" it later: for a mediator
 * west of UTC, ELAPSED ticks over at UTC midnight (5pm Pacific, 8pm Eastern)
 * rather than at their own midnight. That is deliberate. Elapsed, the
 * follow-up date and the overdue badge are all measured on the same clock the
 * server uses, so they can never contradict one another — which matters far
 * more than which side of the evening the counter rolls.
 */

export const MS_PER_DAY = 86400000

/* Only the two the backend accepts (VALID_HOLD_DURATION_UNITS). */
export const HOLD_UNIT_DAYS = { days: 1, weeks: 7 }

/* The backend caps duration_amount at 365 whatever the unit. Weeks get a
 * tighter ceiling here so the stepper cannot build a follow-up date seven
 * years out and have the server happily accept it. */
export const MAX_DURATION_AMOUNT = { days: 365, weeks: 52 }

/* Slot values are "morning" | "afternoon" in the database and always will be,
 * and the label now says the same word. One name for the half-day everywhere:
 * hold screens, schedule board and booking widget. */
export const SLOT_LABEL = { morning: 'Morning', afternoon: 'Afternoon' }

/* The three hold types, escalating in firmness. Same order as the backend's
 * VALID_HOLD_TYPES so the menu reads the way the column does. */
export const HOLD_TYPE_OPTIONS = [
    { value: 'soft', label: 'Soft Hold' },
    { value: 'pending_confirmation', label: 'Pending Confirmation' },
    { value: 'confirmed', label: 'Confirmed' },
]

export function holdTypeLabel(value) {
    return (
        HOLD_TYPE_OPTIONS.find((o) => o.value === value)?.label || 'Soft Hold'
    )
}

/* ---------- UTC day arithmetic ---------- */

/**
 * The UTC calendar day a value falls on, as epoch milliseconds at midnight.
 * Accepts a date-only string or a full ISO timestamp; returns null for
 * anything unparseable (legacy rows carry nulls in every hold column).
 */
export function utcDayMs(value) {
    if (value === null || value === undefined || value === '') return null
    const raw = String(value).trim()
    // A bare date is taken at face value — no Date parsing, so no chance of a
    // runtime deciding "2026-10-18" means local midnight.
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [y, m, d] = raw.split('-').map(Number)
        return Date.UTC(y, m - 1, d)
    }
    const parsed = new Date(raw)
    if (Number.isNaN(parsed.getTime())) return null
    return Date.UTC(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate()
    )
}

/** Midnight of today's UTC calendar day, in epoch milliseconds. */
export function todayUtcMs() {
    const now = new Date()
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
}

/** "YYYY-MM-DD" for a UTC-day timestamp — the shape the backend stores. */
export function isoFromUtcMs(ms) {
    return ms === null || ms === undefined
        ? null
        : new Date(ms).toISOString().slice(0, 10)
}

/* ---------- Formatting (always UTC — see the header) ---------- */

function formatUtc(value, options) {
    const ms = utcDayMs(value)
    if (ms === null) return ''
    return new Date(ms).toLocaleDateString('en-US', {
        ...options,
        timeZone: 'UTC',
    })
}

/** "Oct 2" — abbreviated month, no leading zero. */
export const formatShortDate = (value) =>
    formatUtc(value, { month: 'short', day: 'numeric' })

/** "Thu, Oct 15" — the hold card headline. */
export const formatCardDate = (value) =>
    formatUtc(value, { weekday: 'short', month: 'short', day: 'numeric' })

/** "October 15, 2026" — the hover title, where the year stops being noise. */
export const formatFullDate = (value) =>
    formatUtc(value, { year: 'numeric', month: 'long', day: 'numeric' })

/* ---------- Wording ---------- */

/**
 * "1 day", "3 days", "1 week", "5 weeks". `unit` arrives plural from the API
 * ("days"/"weeks"); the singular is the plural minus its trailing s. Singular
 * correctness is not cosmetic here — "1 days" is the kind of thing a mediator
 * screenshots.
 */
export function durationPhrase(amount, unit) {
    if (!amount || !unit) return null
    return `${amount} ${unitWord(amount, unit)}`
}

/**
 * The unit word for a count — "day" for 1, "days" for anything else.
 *
 * Split out of durationPhrase because a REQUESTED duration of 0 is not a thing
 * anyone can ask for (the stepper floors at 1 and the DB CHECK floors at 1), so
 * durationPhrase is right to treat 0 as "no duration" — but an ELAPSED count of
 * 0 is the normal state of every hold placed today, and must render as "0 days"
 * rather than falling through to the unknown-value em dash.
 */
function unitWord(amount, unit) {
    return amount === 1 ? String(unit).replace(/s$/, '') : String(unit)
}

/** Total calendar days a structured duration spans. */
export function durationDays(amount, unit) {
    const per = HOLD_UNIT_DAYS[unit]
    if (!amount || !per) return null
    return amount * per
}

/**
 * The follow-up date a hold placed right now would get. Mirrors the server's
 * computeHoldExpiresAt exactly: UTC day of the placement stamp, plus the span.
 */
export function previewFollowUpIso(amount, unit, nowMs) {
    const days = durationDays(amount, unit)
    if (days === null) return null
    const base = nowMs === undefined ? todayUtcMs() : nowMs
    return isoFromUtcMs(base + days * MS_PER_DAY)
}

/* ---------- The hold card view model ---------- */

/**
 * One card per actionable hold.
 *
 * A full day whose two halves were held TOGETHER is ONE card labelled "Full
 * day". A day whose halves disagree becomes one card per half, so each can be
 * retyped or released on its own rather than rendering one arbitrary value.
 * Disagreement means either of:
 *
 *   • different hold types — the pre-existing holdRows() rule, kept intact, so
 *     a Confirmed morning and a Soft afternoon never collapse;
 *   • different placement stamps or follow-up dates — halves held in separate
 *     saves are independent holds sharing a date, and one card cannot state two
 *     elapsed counts or two follow-up dates without attributing one half's
 *     numbers to the other (see rowFields below).
 *
 * `is_full_day` from the API still means only "both halves are held", which is
 * what the inquiry list column wants; the extra agreement test lives here
 * because it is a question about cards, not about the day.
 *
 * Everything derived here is derived from what the server actually sent.
 * Legacy holds — 35 rows on UAT, 17 of them with every hold_* column null —
 * must render as calmly as new ones:
 *   • no structured pair  -> REQUESTED falls back to the free-text timeframe
 *   • no free text either -> REQUESTED shows an em dash
 *   • no hold_created_at  -> ELAPSED shows an em dash, never "NaN days"
 *   • no hold_expires_at  -> overdue is undecidable, so the badge stays
 *                            "ON HOLD" and can never read "N DAYS OVER"
 */
export function buildHoldCards(holds, nowMs) {
    const today = nowMs === undefined ? todayUtcMs() : nowMs
    const cards = []
    for (const h of holds || []) {
        const slots = h.slots || []
        if (h.is_full_day && h.hold_type && halvesAgree(slots)) {
            cards.push(
                holdCard(rowFields(h, slots[0]), {
                    key: h.date,
                    slot: null,
                    portionLabel: 'Full day',
                    holdType: h.hold_type,
                    today,
                })
            )
        } else {
            for (const sl of slots) {
                cards.push(
                    holdCard(rowFields(h, sl), {
                        key: `${h.date}|${sl.slot}`,
                        slot: sl.slot,
                        portionLabel: SLOT_LABEL[sl.slot] || sl.slot,
                        holdType: sl.hold_type,
                        today,
                    })
                )
            }
        }
    }
    return cards
}

/**
 * The hold columns that belong to an availability ROW, not to a date.
 *
 * placeHolds writes both halves of a date in one request, so normally the two
 * agree — but holding a morning in one save and the afternoon of the same date in
 * a later save is a fully supported, reachable state (placeHolds only
 * clash-checks the slots actually asked for, and updateHoldType already derives
 * hold_expires_at from each row's own hold_created_at). Those two halves are
 * then independent holds that merely share a date, with their own placement
 * stamps and their own follow-up dates.
 *
 * The API hoists these to the date level as a convenience, taking them from one
 * arbitrary row. That was harmless while they only fed a decorative "Held for
 * {x}" note; it is not harmless now that they drive ELAPSED and the overdue
 * badge — the wrong half's numbers would tell a mediator to chase, or not to
 * chase, the wrong hold. So the per-slot values win wherever the API sends
 * them, and the date-level hoist is only a fallback for an older payload.
 */
const ROW_LEVEL_HOLD_FIELDS = [
    'hold_created_at',
    'hold_expires_at',
    'hold_duration',
    'hold_duration_amount',
    'hold_duration_unit',
    'hold_confirmation_sent_at',
    'hold_expiration_notice_sent_at',
    // Carried because buildStatusSentence needs it: it decides whether the
    // card promises "releases Sep 11" or "held through Sep 11". Left out, it
    // arrives undefined and every card claims the manual wording — including
    // holds that really are on a timer, which is the reading that makes a
    // mediator sit waiting for a date the system already took back.
    'hold_notify_on_expiration',
    'hold_notify_on_confirmation',
]

/** A date entry flattened onto one of its slots. Presence, not truthiness:
 *  a slot that genuinely carries null must not fall back to the other half. */
function rowFields(dateEntry, slotEntry) {
    const out = { date: dateEntry.date }
    for (const k of ROW_LEVEL_HOLD_FIELDS) {
        out[k] =
            slotEntry && k in slotEntry
                ? slotEntry[k]
                : dateEntry[k] === undefined
                  ? null
                  : dateEntry[k]
    }
    return out
}

/**
 * Whether both halves of a date may be collapsed into one "Full day" card.
 *
 * Same type is not enough. Two halves held weeks apart carry different elapsed
 * counts and different follow-up dates, and one card cannot state both — so
 * they split, exactly the way a day with two different hold types already does.
 * An older payload that sends no per-slot stamps reads as agreeing, which
 * restores the previous behaviour rather than fragmenting every card.
 */
function halvesAgree(slots) {
    if (!slots || slots.length < 2) return true
    // The notify flag is part of the signature because it changes what the
    // card SAYS about the date. A morning on a timer and an afternoon the
    // mediator must release by hand are two different situations, and one
    // "Full day" card cannot state both.
    const sig = (s) =>
        `${s.hold_created_at ?? ''}|${s.hold_expires_at ?? ''}|${
            s.hold_duration_amount ?? ''
        }|${s.hold_duration_unit ?? ''}|${s.hold_notify_on_expiration ?? ''}`
    const first = sig(slots[0])
    return slots.every((s) => sig(s) === first)
}

function holdCard(hold, { key, slot, portionLabel, holdType, today }) {
    const createdMs = utcDayMs(hold.hold_created_at)
    const expiresMs = utcDayMs(hold.hold_expires_at)

    const amount = Number.isFinite(hold.hold_duration_amount)
        ? hold.hold_duration_amount
        : null
    const unit = hold.hold_duration_unit || null

    // Structured pair first, free text second, nothing third. The pair is
    // re-phrased rather than echoed so the singular is always right even if an
    // older row stored "1 days" in the free-text column.
    const requestedPhrase =
        durationPhrase(amount, unit) || hold.hold_duration || null

    // Clamped at zero: a clock a few seconds behind the server must not render
    // "-1 days" on a hold placed moments ago.
    const elapsedDays =
        createdMs === null
            ? null
            : Math.max(0, Math.floor((today - createdMs) / MS_PER_DAY))

    const isOverdue = expiresMs !== null && today > expiresMs
    const daysOver = isOverdue
        ? Math.floor((today - expiresMs) / MS_PER_DAY)
        : 0

    return {
        key,
        date: hold.date,
        slot,
        portionLabel,
        holdType: holdType || 'soft',
        requestedPhrase,
        requestedParts: splitPhrase(requestedPhrase),
        elapsedDays,
        // Built directly rather than through durationPhrase/splitPhrase: a hold
        // placed today has elapsed 0 days, and 0 is a real answer. Routing it
        // through the phrase helpers turned it into a missing value and the
        // card rendered the unknown-state em dash for the whole session in
        // which the hold was created.
        elapsedParts:
            elapsedDays === null
                ? null
                : {
                      value: String(elapsedDays),
                      unit: unitWord(elapsedDays, 'days'),
                  },
        isOverdue,
        daysOver,
        badgeLabel: isOverdue
            ? `${daysOver} DAY${daysOver === 1 ? '' : 'S'} OVER`
            : 'ON HOLD',
        statusSentence: buildStatusSentence(hold),
    }
}

/**
 * "3 days" -> { value: "3", unit: "days" } so the number can be set heavier
 * than the unit. A free-text timeframe that is not a plain number-and-word
 * ("till they confirm funding") stays whole in `unit` with no number, which is
 * why the value half is allowed to be empty.
 */
function splitPhrase(phrase) {
    if (!phrase) return null
    const m = String(phrase).match(/^(\d+(?:\.\d+)?)\s+(.+)$/)
    return m ? { value: m[1], unit: m[2] } : { value: '', unit: String(phrase) }
}

/**
 * The one muted line under the statistics.
 *
 * Two different kinds of clause live here and they are gated differently:
 *
 *   • A "sent" clause is a claim that an email went out, so it is built only
 *     around a timestamp that exists. Nothing prints "sent" next to an empty
 *     date.
 *
 *   • The release clause is not a claim about email at all — it is the date the
 *     ELAPSED figure is being measured against, and the only place on the card
 *     that date appears. It stands on its own and is NOT gated on a
 *     confirmation having been sent.
 *
 *     Its wording follows the expiration switch, because the switch decides
 *     whether anything acts on that date. On, the sweep clears the hold when it
 *     arrives, so the card says "releases". Off, nothing happens without the
 *     mediator, so it says "held through" — a card promising a release that
 *     will not come would leave someone waiting for it.
 *
 *   nothing to say                -> null (no line)
 *   expiry, switch on             -> "Releases Sep 11"
 *   expiry, switch off            -> "Held through Sep 11"
 *   confirmation + expiry on      -> "Confirmation sent Aug 26 · releases Sep 11"
 *   confirmation only             -> "Confirmation sent Aug 26"
 *
 * A hold with no expiry date shows no clause at all, which is correct — a
 * free-text duration produces no expiry and the sweep never touches it.
 */
function buildStatusSentence(hold) {
    const parts = []
    // Lower-cased when it follows another clause, capitalised when it opens the
    // sentence on its own.
    const lead = (word) => (parts.length ? word : capitalise(word))

    if (hold.hold_confirmation_sent_at) {
        parts.push(
            `Confirmation sent ${formatShortDate(hold.hold_confirmation_sent_at)}`
        )
    }
    if (hold.hold_expires_at) {
        // "Releases" is a promise the system only keeps when the expiration
        // switch is on. With it off nothing acts on this date, so the card must
        // not imply a release is coming — the mediator is the one who ends it,
        // and a card that said otherwise would have them waiting.
        parts.push(
            hold.hold_notify_on_expiration
                ? `${lead('releases')} ${formatShortDate(hold.hold_expires_at)}`
                : `${lead('held through')} ${formatShortDate(hold.hold_expires_at)}`
        )
    }
    return parts.length ? parts.join(' · ') : null
}

const capitalise = (s) => s.charAt(0).toUpperCase() + s.slice(1)
